import moment from 'moment';
import api from '@/api/users';
// eslint-disable-next-line import/no-cycle
import { resetState } from '@/store';
import { DEFAULT_TIMEZONE } from '@/constants';

const { SKIP_PENDO_INITIALIZATION } = process.env;

export const getters = {
  userProfile: ({ userProfile }) => userProfile,
  userTimezone: ({ userProfile }) => (userProfile ? userProfile.timezone : null),
  userTimezoneOrDefault: (state, existingGetters) => existingGetters.userTimezone || DEFAULT_TIMEZONE,
  isAuthenticated: ({ isAuthenticated }) => isAuthenticated,
  token: ({ token }) => token,
};

export const mutations = {
  SET_USER_AUTHENTICATION(state, { token, userId }) {
    if (token && userId) {
      state.isAuthenticated = true;
      state.token = token;
      localStorage.token = token;
      localStorage.userId = userId;
    } else {
      state.isAuthenticated = false;
      state.token = null;
      localStorage.clear();
      resetState();
    }
  },

  SET_USER_PROFILE(state, { userProfile }) {
    state.userProfile = userProfile;
    // set user session data for Datadog RUM tracing
    window.DD_RUM.setUser({
      id: userProfile.id,
      email: userProfile.email,
      organization_id: userProfile.organization.id,
      group_id: userProfile.group.id,
      group_name: userProfile.group.name,
    });
  },

  SET_USER_TIMEZONE(state, { timezone }) {
    state.userProfile.timezone = timezone;
  },
};

export const actions = {
  LOGOUT: ({ commit, dispatch }) => {
    commit('SET_USER_AUTHENTICATION', { token: false, userId: false });
    dispatch('INITIALIZE_PENDO', { userProfile: null });
  },

  UPDATE_USER_TIMEZONE: async ({ state, commit }, timezone) => {
    await api.setUserTimeZone({ id: state.userProfile.id, timezone });
    commit('SET_USER_TIMEZONE', { timezone });
  },

  TOKEN_AUTHENTICATION: async ({ dispatch, commit }, { token }) => {
    try {
      const result = await api.tokenAuthentication(token);
      const newToken = result.token;
      const newUserId = result.user_id;

      commit('SET_USER_AUTHENTICATION', { token: newToken, userId: newUserId });
      dispatch('FETCH_USER_PROFILE', { userId: newUserId });
    } catch (error) {
      throw new Error(error);
    }
  },

  USER_AUTHENTICATION: async ({ dispatch, commit }, { username, password }) => {
    try {
      const result = await api.authenticate(username, password);
      const { token } = result;
      const userId = result.user_id;

      commit('SET_USER_AUTHENTICATION', { token, userId });
      dispatch('FETCH_USER_PROFILE', { userId });
    } catch (error) {
      throw new Error(error);
    }
  },

  FETCH_USER_PROFILE: async ({ dispatch, commit }, { userId }) => {
    try {
      const userProfile = await api.fetchUserProfile(userId);
      dispatch('INITIALIZE_PENDO', { userProfile });
      dispatch('FETCH_FEATURE_FLAGS');
      commit('SET_USER_PROFILE', { userProfile });
    } catch (error) {
      throw new Error(error);
    }
  },

  INITIALIZE_PENDO: (context, { userProfile }) => {
    if (SKIP_PENDO_INITIALIZATION) {
      return;
    }

    if (!userProfile) {
      pendo.initialize({ visitor: {} }); // eslint-disable-line no-undef
      return;
    }

    const visitor = {};
    const account = {};

    if (userProfile.id) {
      visitor.id = userProfile.id;
    }
    if (userProfile.email) {
      visitor.email = userProfile.email;
    }
    if (userProfile.organization && userProfile.organization.id) {
      visitor.organizationId = userProfile.organization.id;
      account.id = userProfile.organization.id;
    }
    if (userProfile.group && userProfile.group.id) {
      visitor.featureGroupId = userProfile.group.id;
    }
    if (userProfile.membership_start_date) {
      visitor.membershipStartDate = moment(userProfile.membership_start_date).format('X');
    }

    pendo.initialize({
      // eslint-disable-line no-undef
      visitor,
      account,
    });
  },
};

const state = {
  isAuthenticated: false,
  userProfile: {},
  token: null,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
